import { produce } from 'immer';
import {
  createContext,
  useReducer,
  useEffect,
  useContext,
  type ReactNode,
} from 'react';
import { useLocation, useLoaderData } from 'react-router-dom';

import { PAGE_SIZE, CATEGORY_SIDE_BAR_SIZE } from 'src/core/domain/constants';
import { getPageNumber } from 'src/core/utils/pageUtil';
import { useAuthStatePageReload } from 'src/hooks/useAuthPageReload/useAuthPageReload';
import type { CategoryGroupsLoaderData } from 'src/mapper/groupMapper';
import type { CommunityGroupsLoader } from 'src/pages/CommunityGroups';

interface GroupState {
  totalPage: number;
  myCategoryTotalPage: number;
  category: CategoryGroupsLoaderData[];
  myCategory: CategoryGroupsLoaderData[];
  sideBarMyGroupCategory: CategoryGroupsLoaderData[];
  sideBarCategory: CategoryGroupsLoaderData[];
  currentPageCategory: CategoryGroupsLoaderData[];
  myCurrentPageCategory: CategoryGroupsLoaderData[];
}

type GroupAction =
  | { type: 'PAGE_CHANGE'; payload: { pageNumber: number } }
  | {
      type: 'RELOAD_ALL_CATEGORY';
      payload: {
        category: CategoryGroupsLoaderData[];
        currentPageCategory: CategoryGroupsLoaderData[];
        myCategory: CategoryGroupsLoaderData[];
        myCurrentPageCategory: CategoryGroupsLoaderData[];
      };
    }
  | {
      type: 'RELOAD_CURRENT_CATEGORY';
      payload: {
        currentPageCategory: CategoryGroupsLoaderData[];
        myCurrentPageCategory: CategoryGroupsLoaderData[];
      };
    }
  | {
      type: 'UPDATE_FOLLOW_STATUS';
      payload: { selectedCategoryId: string; status: boolean };
    };

interface GroupContextType {
  groupState: GroupState;
  groupDispatch: React.Dispatch<GroupAction>;
}

const GroupContext = createContext<GroupContextType | undefined>(undefined);

interface GroupProviderProps {
  children: ReactNode;
  value: CategoryGroupsLoaderData[];
}

const getGroupList = (
  category: CategoryGroupsLoaderData[],
  pageNumber: number = 1,
): {
  sideBarMyGroupCategory: CategoryGroupsLoaderData[];
  sideBarCategory: CategoryGroupsLoaderData[];
  myCategory: CategoryGroupsLoaderData[];
  myCurrentPageCategory: CategoryGroupsLoaderData[];
  myCategoryTotalPage: number;
} => {
  if (category.length === 0) {
    return {
      sideBarMyGroupCategory: [],
      sideBarCategory: [],
      myCategory: [],
      myCurrentPageCategory: [],
      myCategoryTotalPage: 1,
    };
  }

  const myCategory = category.filter(({ followed }) => followed);
  const sideBarMyGroupCategory = category
    .filter(({ followed }) => followed)
    .slice(0, CATEGORY_SIDE_BAR_SIZE);
  const myGroupIds = sideBarMyGroupCategory.map(({ categoryId }) => categoryId);
  const sideBarCategory = category
    .filter(({ categoryId }) => myGroupIds.indexOf(categoryId) < 0)
    .slice(0, CATEGORY_SIDE_BAR_SIZE);

  const myCurrentPageCategory = myCategory.slice(
    PAGE_SIZE * (pageNumber - 1),
    PAGE_SIZE * pageNumber,
  );
  const myCategoryTotalPage = Math.ceil(myCategory.length / PAGE_SIZE);

  return {
    myCategory,
    sideBarMyGroupCategory,
    sideBarCategory,
    myCurrentPageCategory,
    myCategoryTotalPage,
  };
};

const groupReducer = (state: GroupState, action: GroupAction): GroupState => {
  const { type, payload } = action;
  switch (type) {
    case 'PAGE_CHANGE':
      return produce(state, (draft) => {
        draft.currentPageCategory = state.category.slice(
          PAGE_SIZE * (payload.pageNumber - 1),
          PAGE_SIZE * payload.pageNumber,
        );
        draft.myCurrentPageCategory = state.myCategory.slice(
          PAGE_SIZE * (payload.pageNumber - 1),
          PAGE_SIZE * payload.pageNumber,
        );
      });
    case 'RELOAD_ALL_CATEGORY':
      return produce(state, (draft) => {
        const {
          category,
          currentPageCategory,
          myCategory,
          myCurrentPageCategory,
        } = payload;
        draft.category = category;
        const { sideBarMyGroupCategory, sideBarCategory } =
          getGroupList(category);
        draft.myCategory = myCategory;
        draft.sideBarMyGroupCategory = sideBarMyGroupCategory;
        draft.sideBarCategory = sideBarCategory;
        draft.currentPageCategory = currentPageCategory;
        draft.myCategory = myCategory;
        draft.myCurrentPageCategory = myCurrentPageCategory;
        draft.myCategoryTotalPage = Math.ceil(myCategory.length / PAGE_SIZE);
      });
    case 'RELOAD_CURRENT_CATEGORY':
      return produce(state, (draft) => {
        const {
          currentPageCategory: currentCategory,
          myCurrentPageCategory: myCurrentCategory,
        } = payload;
        draft.currentPageCategory = currentCategory;
        draft.myCurrentPageCategory = myCurrentCategory;
      });
    case 'UPDATE_FOLLOW_STATUS':
      const { selectedCategoryId, status } = payload;

      return produce(state, (draft) => {
        const updateCategory = draft.category.find(
          ({ categoryId }) => categoryId === selectedCategoryId,
        );
        if (updateCategory) {
          updateCategory.followed = status;
          const {
            sideBarMyGroupCategory,
            sideBarCategory,
            myCurrentPageCategory,
            myCategoryTotalPage,
          } = getGroupList(draft.category);
          draft.sideBarMyGroupCategory = sideBarMyGroupCategory;
          draft.sideBarCategory = sideBarCategory;
          draft.myCurrentPageCategory = myCurrentPageCategory;
          draft.myCategoryTotalPage = myCategoryTotalPage;
        }
        const updateCurrentCategory = draft.currentPageCategory.find(
          ({ categoryId }) => categoryId === selectedCategoryId,
        );
        if (updateCurrentCategory) {
          updateCurrentCategory.followed = status;
        }
      });
    default:
      throw new Error('GroupReducer Unknown action type');
  }
};

const useRefreshGroups = (
  groupDispatch: React.Dispatch<GroupAction>,
  categoryGroups: CategoryGroupsLoaderData[],
) => {
  const { search } = useLocation();
  const currentPage = getPageNumber(search);

  // Reload all categories when the category list is updated
  useEffect(() => {
    groupDispatch({
      type: 'RELOAD_ALL_CATEGORY',
      payload: {
        category: categoryGroups,
        currentPageCategory: categoryGroups.slice(
          PAGE_SIZE * (currentPage - 1),
          PAGE_SIZE * currentPage,
        ),
        myCategory: categoryGroups.filter(({ followed }) => followed),
        myCurrentPageCategory: categoryGroups
          .filter(({ followed }) => followed)
          .slice(PAGE_SIZE * (currentPage - 1), PAGE_SIZE * currentPage),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryGroups, groupDispatch]);
  // Reload current category when the page number is changed
  useEffect(() => {
    groupDispatch({
      type: 'RELOAD_CURRENT_CATEGORY',
      payload: {
        currentPageCategory: categoryGroups.slice(
          PAGE_SIZE * (currentPage - 1),
          PAGE_SIZE * currentPage,
        ),
        myCurrentPageCategory: categoryGroups
          .filter(({ followed }) => followed)
          .slice(PAGE_SIZE * (currentPage - 1), PAGE_SIZE * currentPage),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, groupDispatch]);
};

export const GroupProvider: React.FC<GroupProviderProps> = ({
  children,
  value,
}) => {
  const { init, isAuthenticated } = useLoaderData() as CommunityGroupsLoader;
  const { search } = useLocation();
  const currentPage = getPageNumber(search);

  const [groupState, groupDispatch] = useReducer(groupReducer, {
    totalPage: value.length / PAGE_SIZE,
    category: value,
    ...getGroupList(value, currentPage),
    currentPageCategory: value.slice(
      PAGE_SIZE * (currentPage - 1),
      PAGE_SIZE * currentPage,
    ),
  });

  useAuthStatePageReload(init, isAuthenticated);
  useRefreshGroups(groupDispatch, value);

  return (
    <GroupContext.Provider
      value={{
        groupState,
        groupDispatch,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};

export const useGroup = (): GroupContextType => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within a GroupProvider');
  }
  return context;
};

export default GroupProvider;
